import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactCTA = () => {
  const stats = [
    { number: "50+", label: "Projects Completed", icon: "Briefcase" },
    { number: "98%", label: "Client Satisfaction", icon: "Heart" },
    { number: "24/7", label: "Support Available", icon: "Clock" },
    { number: "2hrs", label: "Average Response", icon: "Zap" }
  ];
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const guarantees = [
    {
      icon: "Shield",
      title: "100% Confidentiality",
      description: "Your ideas and data are protected with enterprise-grade security"
    },
    {
      icon: "Award",
      title: "Quality Guarantee",
      description: "We stand behind our work with comprehensive quality assurance"
    },
    {
      icon: "Users",
      title: "Dedicated Team",
      description: "Your project gets a dedicated team of experts from start to finish"
    },
    {
      icon: "Rocket",
      title: "Fast Delivery",
      description: "Agile development process ensures rapid iteration and delivery"
    }
  ];
  const loadCalendlyScript = () => {
    return new Promise((resolve, reject) => {
      if (document.getElementById("calendly-widget-script")) {
        resolve(true);
        return;
      }

      const link = document.createElement("link");
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);

      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.id = "calendly-widget-script";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => reject(false);

      document.body.appendChild(script);
    });
  };

  const openCalendly = async () => {
    if (!scriptLoaded) {
      try {
        await loadCalendlyScript();
        setScriptLoaded(true);
      } catch (err) {
        console.error("Calendly script failed to load");
        return;
      }
    }

    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/shutterlifters/30min",
      });
    } else {
      console.error("Calendly object not available");
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/5 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-accent/10 rounded-full blur-xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-48 h-48 bg-success/10 rounded-full blur-xl"
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1, 0.8, 1]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Stats Section */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {stats?.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center group"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <motion.div
                className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-colors duration-300"
                whileHover={{ rotateY: 180 }}
                transition={{ duration: 0.6 }}
              >
                <Icon name={stat?.icon} size={24} className="text-accent" />
              </motion.div>
              <motion.div
                className="text-3xl md:text-4xl font-bold text-foreground mb-2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
              >
                {stat?.number}
              </motion.div>
              <p className="text-muted-foreground text-sm">{stat?.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Main CTA */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-6xl font-bold text-foreground mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Ready to Start Your
            <motion.span
              className="block text-accent"
              animate={{
                textShadow: [
                  "0 0 20px rgba(0, 212, 255, 0.5)",
                  "0 0 40px rgba(0, 212, 255, 0.8)",
                  "0 0 20px rgba(0, 212, 255, 0.5)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Digital Journey?
            </motion.span>
          </motion.h2>

          <motion.p
            className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Join hundreds of satisfied clients who have transformed their businesses with our innovative solutions.
            Let's discuss how we can bring your vision to life.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <Button
              variant="default"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent px-8 py-4"
              onClick={openCalendly}
            >
              Start Free Consultation
            </Button>


            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="border-accent/30 text-accent px-8 py-4"
            >
              Call Now: +91 7579505033
            </Button>
          </motion.div>

          <motion.div
            className="flex items-center justify-center space-x-6 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span>Free consultation</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-success" />
              <span>2-hour response</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span>100% confidential</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Guarantees */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {guarantees?.map((guarantee, index) => (
            <motion.div
              key={index}
              className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-6 text-center hover:border-accent/50 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                rotateY: 5
              }}
            >
              <motion.div
                className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4"
                whileHover={{ rotateY: 360 }}
                transition={{ duration: 0.8 }}
              >
                <Icon name={guarantee?.icon} size={20} className="text-accent" />
              </motion.div>
              <h3 className="font-semibold text-foreground mb-2">{guarantee?.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {guarantee?.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Emergency Contact */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Icon name="AlertCircle" size={24} className="text-destructive" />
              <h3 className="text-xl font-bold text-foreground">Emergency Support</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              Need urgent technical assistance? Our emergency support team is available 24/7 for critical issues.
            </p>
            <Button
              variant="destructive"
              size="sm"
              iconName="Phone"
              iconPosition="left"
            >
              Emergency Hotline: (+91) 7579505033
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactCTA;